import {
  IMultipleChoiceItem,
  IMultipleChoiceTask,
  IMultipleChoiceTaskItemSession,
  IMultipleChoiceTaskSession,
  tMultipleChoiceAnswer
} from 'common/modules/ReactShared';
import React, { useEffect, useState } from 'react';
import { usePromptContext } from 'students/views/shared/hooks';
import {
  BaseCheckTaskBody,
  CheckNavFooter,
  SCheckTaskWrapper,
  SItemTitle,
  SItemWrapper
} from '../../components';
import { isLessonCompleted } from '../../helpers';
import { useTaskItems } from '../../useTaskItems';
import { ICheckTaskProps } from '../types';
import MultipleChoiceOptions, { ISelectAnswerPayload } from './MultipleChoiceOptions';

const CheckMultipleChoiceTask: React.FC<
  ICheckTaskProps<IMultipleChoiceTask, IMultipleChoiceTaskSession>
> = ({ task, taskSession, isCompleting, onNext, previousTaskId }) => {
  const { makeDirty } = usePromptContext();
  const [answers, setAnswers] = useState<tMultipleChoiceAnswer>([]);
  const { items } = task;
  const { taskItems, sessionTaskItems } = useTaskItems<
    IMultipleChoiceItem,
    IMultipleChoiceTaskItemSession
  >(items, taskSession?.taskItemSessions);

  useEffect(() => {
    if (sessionTaskItems.length) {
      setAnswers(sessionTaskItems);
    } else {
      setAnswers(
        taskItems.map((item, index) => {
          return {
            completed: false,
            id: index,
            taskItemId: item.id,
            options: item.options.map((option) => ({
              taskItemOptionId: option.id,
              answer: null
            })),
            mistakesCount: 0,
            skipped: false,
            url: ''
          };
        })
      );
    }
  }, [taskItems, sessionTaskItems]);

  function handleAnswerChange(payload: ISelectAnswerPayload) {
    const { itemId, optionId, isRadio } = payload;

    const answerIndex = answers.findIndex((a) => a.taskItemId === itemId);
    if (answerIndex < 0) return;

    const optionIndex = answers[answerIndex].options.findIndex(
      (o) => o.taskItemOptionId === optionId
    );
    if (optionIndex < 0) return;

    const answer = answers[answerIndex];

    if (isRadio) {
      setAnswers([
        ...answers.slice(0, answerIndex),
        {
          ...answer,
          options: [
            ...answer.options
              .slice(0, optionIndex)
              .map((option) => ({ ...option, answer: false })),
            {
              ...answer.options[optionIndex],
              answer: true
            },
            ...answer.options
              .slice(optionIndex + 1)
              .map((option) => ({ ...option, answer: false }))
          ],
          skipped: false
        },
        ...answers.slice(answerIndex + 1)
      ]);
    } else {
      setAnswers([
        ...answers.slice(0, answerIndex),
        {
          ...answer,
          options: [
            ...answer.options.slice(0, optionIndex),
            {
              ...answer.options[optionIndex],
              answer: !answer.options[optionIndex].answer
            },
            ...answer.options.slice(optionIndex + 1)
          ],
          skipped: false
        },
        ...answers.slice(answerIndex + 1)
      ]);
    }

    makeDirty(true);
  }

  const handleNext = () => {
    onNext(answers);
  };

  const handleSkip = () => {
    onNext(
      answers.map((answer: IMultipleChoiceTaskItemSession) => ({
        ...answer,
        answers: [],
        skipped: true
      }))
    );
  };

  if (!answers.length) return null;

  return (
    <>
      <SCheckTaskWrapper>
        <BaseCheckTaskBody task={task}>
          {taskItems.map((item: IMultipleChoiceItem, index: number) => (
            <SItemWrapper key={item.id} dir="ltr">
              <SItemTitle>{item.question}</SItemTitle>
              {item?.options?.length ? (
                <MultipleChoiceOptions
                  itemId={item.id}
                  options={item?.options}
                  answers={answers[index]}
                  singleChoice={item.singleChoice}
                  onAnswerChange={handleAnswerChange}
                />
              ) : null}
            </SItemWrapper>
          ))}
        </BaseCheckTaskBody>
      </SCheckTaskWrapper>

      <CheckNavFooter
        isCompleting={isCompleting}
        onNext={handleNext}
        onSkip={handleSkip}
        isLessonCompleted={isLessonCompleted(taskSession)}
        previousTaskId={previousTaskId}
      />
    </>
  );
};

export default CheckMultipleChoiceTask;
